import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type SignInInput, signInInput } from "@tycho/shared";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authClient, signIn } from "@/lib/auth-client";

export const Route = createFileRoute("/login")({
  // Typed search params — this is the TanStack Router payoff.
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ search }) => {
    const { data } = await authClient.getSession();
    if (data) throw redirect({ to: search.redirect ?? "/agents" });
  },
  component: Login,
});

function Login() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<SignInInput>({
    resolver: zodResolver(signInInput),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const { error } = await signIn.email(values);
    if (error) {
      // Better Auth's messages are English; map the one code a user can act on
      // and keep the rest generic rather than showing mixed-language text.
      setFormError(
        error.code === "INVALID_EMAIL_OR_PASSWORD"
          ? "E-Mail oder Passwort ist falsch."
          : "Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      );
      return;
    }
    await navigate({ to: search.redirect ?? "/agents" });
  });

  return (
    <div className="flex flex-1 items-center justify-center p-4 pb-[12vh] md:p-8 md:pb-[12vh]">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1.5 text-center">
          <h1 className="font-serif text-3xl tracking-tight">Anmelden</h1>
          <p className="text-muted-foreground text-sm">
            Melden Sie sich an, um mit Tycho weiterzuarbeiten.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border bg-background p-6"
          noValidate
        >
          <Field label="E-Mail" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              className="h-10"
              {...form.register("email")}
            />
          </Field>
          <Field
            label="Passwort"
            htmlFor="password"
            error={form.formState.errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              className="h-10"
              {...form.register("password")}
            />
          </Field>
          {formError ? (
            <p role="alert" className="text-destructive text-sm">
              {formError}
            </p>
          ) : null}
          <Button type="submit" className="w-full md:h-10" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? "Anmeldung läuft…" : "Anmelden"}
          </Button>
        </form>
      </div>
    </div>
  );
}
